import React from 'react';
import {Trans, useTranslation} from 'react-i18next';

import {Modal, ModalActions, ModalHeader, ModalSubmitButton, showModal} from '@edna/components';
import {Divider, Icon, SkeletonItem, Stack} from '@edna/components/primitives';
import {useMeasure} from '@edna/components/utils';
import {ECurrencySymbol} from '@edna/utils';

import {InlineLink, InlineLinkButton, Text} from 'src/components/primitives';
import {EOperatorName, EmptyIcon, OPERATOR_ICONS} from 'src/constants';
import {TTariffPricingDetail} from 'src/containers/TariffsCatalog';
import {useAppearStyles, useCountryFlags, useEdnaLinks} from 'src/hooks';
import {PhoneDeviceIcon} from 'src/icons';
import {getCountryLabel} from 'src/utils';

import {useGetSmsTariffPricingQuery} from '../../api';
import {TAvailableTariff} from '../../definitions';
import * as SS from '../style';
import {SmsOperatorPricing} from './SmsOperatorPricing';
import * as S from './style';
import {useLicensePrices} from './usePrices';

type TPricingCardProps = {
  icon?: TSvgComponentType;
  label?: string;
  currency: string;
  pricingLabel: string;
  pricingDetail: TTariffPricingDetail;
};

const PricingCard = React.memo<TPricingCardProps>(
  ({icon, label, pricingLabel, currency, pricingDetail}) => {
    const {t} = useTranslation();
    const [isOpenMore, setIsOpenMore] = React.useState(false);
    const [isVisibleMore, setIsVisibleMore] = React.useState(false);
    const [childrenRef, {height: childrenHeight}] = useMeasure<HTMLDivElement>();

    const handleOpen = () => {
      setIsOpenMore(true);
      setIsVisibleMore((previous) => !previous);
    };

    const styles = useAppearStyles({
      isVisible: isVisibleMore,
      childrenHeight,
      onClose: () => setIsOpenMore(false),
    });

    return (
      <S.Card>
        <React.Suspense fallback={null}>{!!icon && <Icon as={icon} size="20px" />}</React.Suspense>
        <Stack isFullWidth>
          <Text bottomIndent="2">{label}</Text>
          <Text secondary size="S" bottomIndent="2">
            {t('Tariffs:smsPricing.pricingType')}
          </Text>
          <Text bottomIndent="1">{pricingLabel}</Text>
          <SS.More onClick={handleOpen}>
            <SS.MoreLabel>{t('Tariffs:tariffCard.pricing')}</SS.MoreLabel>
            <SS.MoreIcon isOpen={isVisibleMore} />
          </SS.More>
          {isOpenMore && (
            <S.Animated style={styles}>
              <div ref={childrenRef}>
                <SmsOperatorPricing pricingDetail={pricingDetail} currency={currency} />
              </div>
            </S.Animated>
          )}
        </Stack>
      </S.Card>
    );
  },
);

PricingCard.displayName = 'PricingCard';

type TModalProps = {
  subjectId: number;
  closeModal: TEmptyFunction;
};

const getOperatorIcon = (operatorName: string) => {
  const OperatorIcon = OPERATOR_ICONS[operatorName as EOperatorName];

  return OperatorIcon === EmptyIcon ? PhoneDeviceIcon : OperatorIcon;
};

const SmsPricingModal = React.memo<TModalProps>(({closeModal, subjectId}) => {
  const {t} = useTranslation();

  const {data: pricing} = useGetSmsTariffPricingQuery(subjectId);

  const countryFlags = useCountryFlags(pricing?.countries);

  return (
    <Modal width="830px">
      <ModalHeader>{t('Tariffs:smsPricing.modalTitle')}</ModalHeader>
      <Stack size="2" isFullWidth>
        <S.ScrollContainer>
          {pricing?.operators.map((detail) => (
            <PricingCard
              key={`${detail.id} ${detail.version}`}
              icon={getOperatorIcon(detail.label)}
              label={t(`Common:operator.${detail.label}`)}
              pricingDetail={detail.pricingDetails[0]}
              currency={ECurrencySymbol[detail.currency]}
              pricingLabel={t(`SmsChannel:options.${detail.pricingDetails[0].label}`)}
            />
          ))}
          {pricing?.countries.map((detail) => (
            <PricingCard
              key={`${detail.id} ${detail.version}`}
              icon={countryFlags[detail.country]}
              label={getCountryLabel(t, detail.country)}
              pricingDetail={detail.pricingDetails[0]}
              currency={ECurrencySymbol[detail.currency]}
              pricingLabel={t(`SmsChannel:options.${detail.pricingDetails[0].label}`)}
            />
          ))}
        </S.ScrollContainer>
        <Divider />
        <Text secondary size="XS">
          {t('Tariffs:pricing.vatHint')}
        </Text>
      </Stack>
      <ModalActions>
        <ModalSubmitButton onClick={closeModal} appearance="secondary">
          {t('Common:button.close')}
        </ModalSubmitButton>
      </ModalActions>
    </Modal>
  );
});

SmsPricingModal.displayName = 'SmsPricingModal';

const showSmsPricingModal = (subjectId: TModalProps['subjectId']) =>
  showModal((closeModal) => <SmsPricingModal closeModal={closeModal} subjectId={subjectId} />);

type TSmsPricingProps = {
  subjectId?: number;
  tariff: TAvailableTariff;
};

const SmsPricing = React.memo<TSmsPricingProps>(({subjectId, tariff}) => {
  const {t} = useTranslation();
  const links = useEdnaLinks();

  const licensePrices = useLicensePrices(tariff);

  if (!licensePrices || !tariff) {
    return (
      <S.LoadingContainer>
        <SkeletonItem height="50px" />
        <SkeletonItem height="50px" />
      </S.LoadingContainer>
    );
  }

  return (
    <S.Pricing>
      <Text weight={500}>
        {t('Tariffs:smsPricing.licensePrice')}
        <Text secondary size="S" topIndent="1">
          {t('Tariffs:smsPricing.licensePriceHint', {
            currency: licensePrices.currency,
            licensePrice: licensePrices.licensePrice,
          })}
        </Text>
      </Text>
      <Text weight={500}>
        {t('Tariffs:smsPricing.detailPricing')}
        <Text secondary size="S" topIndent="1">
          <Trans i18nKey="Tariffs:smsPricing.detailPricingHint">
            {subjectId ? (
              <InlineLinkButton onClick={() => showSmsPricingModal(subjectId)} />
            ) : (
              <InlineLink href={links.SMS_PRICING} />
            )}
          </Trans>
        </Text>
      </Text>
      <Text secondary size="XS">
        <Trans i18nKey="Tariffs:smsPricing.pricingHint" />
      </Text>
    </S.Pricing>
  );
});

SmsPricingModal.displayName = 'SmsPricingModal';

export {SmsPricing};
